import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CTASectionProps {
  isAuthenticated?: boolean;
}

const CTASection = ({ isAuthenticated = false }: CTASectionProps) => {
  return (
    <section className="py-16 lg:py-24 bg-gradient-to-r from-primary to-primary/80 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-5xl font-heading font-bold text-primary-foreground leading-tight">
                Ready to Transform Your Space?
              </h2>
              <p className="text-lg text-primary-foreground/90 leading-relaxed">
                Join thousands of users who have maximized their outdoor spaces for food production and income generation. Get your personalized optimization plan in minutes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href={isAuthenticated ? "/input-form" : "/register"}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200 shadow-lg">

                <Icon name="RocketLaunchIcon" size={20} className="mr-2" />
                Get Started Free
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-primary-foreground font-semibold rounded-lg hover:bg-white/10 transition-colors duration-200">

                Sign In
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-6">
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="ShieldCheckIcon" size={20} />
                <span className="text-sm">SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="UserGroupIcon" size={20} />
                <span className="text-sm">2.5K+ Users</span>
              </div>
              <div className="flex items-center space-x-2 text-primary-foreground/80">
                <Icon name="StarIcon" size={20} />
                <span className="text-sm">4.9/5 Rating</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://images.unsplash.com/photo-1592863690126-9063a03c9139"
                alt="Person tending to thriving vertical garden with tomatoes and herbs on apartment balcony with city skyline background"
                className="w-full h-[400px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Success Metric */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-card border border-border rounded-lg p-4 shadow-xl">
              <div className="text-center">
                <div className="text-2xl font-bold text-success">$850</div>
                <div className="text-sm text-muted-foreground">Average Annual Income</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CTASection;